import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Button, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import JoditEditor from 'jodit-react';
import Services from '../../network/Services';

// Helper function to check if HTML content is empty (only tags, no actual text)
const isHtmlEmpty = (html) => {
  if (!html) return true;
  // Remove all HTML tags
  let stripped = html.replace(/<[^>]*>/g, '');
  // Remove HTML entities like &nbsp;
  stripped = stripped.replace(/&nbsp;/g, ' ');
  // Remove all whitespace and check if anything remains
  stripped = stripped.trim();
  return stripped.length === 0;
};

const joditConfig = {
  readonly: false,
  height: 220,
  toolbarButtonSize: 'small',
  buttons: [
    'bold', 'italic', 'underline', '|',
    'ul', 'ol', '|',
    'link', '|',
    'paragraph', '|',
    'undo', 'redo',
  ],
  removeButtons: ['image', 'video', 'file', 'speechRecognize', 'spellcheck', 'about', 'fullsize', 'print'],
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultActionOnPaste: 'insert_clear_html',
  style: { fontSize: '14px' },
};

const AddInfoModal = ({ visible, onClose, onSubmit, editData, onDelete }) => {
  const editorRef = useRef(null);
  const [title, setTitle] = useState('');
  const [titleBackgroundColor, setTitleBackgroundColor] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [subTitleBackgroundColor, setSubTitleBackgroundColor] = useState('');
  const [description, setDescription] = useState('');
  const [descriptionBackgroundColor, setDescriptionBackgroundColor] = useState('');
  const [subDescription, setSubDescription] = useState('');
  const [subDescriptionBackgroundColor, setSubDescriptionBackgroundColor] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [screenOptions, setScreenOptions] = useState([]);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [screenLoading, setScreenLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const isEdit = !!editData;

  useEffect(() => {
    if (visible) {
      setErrors({});
      setTitle(isEdit ? editData.title || '' : '');
      setTitleBackgroundColor(isEdit ? editData.titleBackgroundColor || '' : '');
      setSubTitle(isEdit ? editData.subTitle || '' : '');
      setSubTitleBackgroundColor(isEdit ? editData.subTitleBackgroundColor || '' : '');
      setDescription(isEdit ? editData.description || '' : '');
      setDescriptionBackgroundColor(isEdit ? editData.descriptionBackgroundColor || '' : '');
      setSubDescription(isEdit ? editData.subDescription || '' : '');
      setSubDescriptionBackgroundColor(isEdit ? editData.subDescriptionBackgroundColor || '' : '');
      setSelectedScreen(null);
      fetchScreenNames();
    }
  }, [visible, editData]);

  const fetchScreenNames = async () => {
    setScreenLoading(true);
    try {
      const subtenantId = sessionStorage?.getItem('subtenantId') || '2';
      const res = await Services.GetCommMediumPathApi2(true, subtenantId);
      const data = res?.data?.data || [];
      const options = data?.filter(item => item.isActive && item.comMediumContent?.displayContent)?.map(item => ({
        value: item.commMediumId,
        label: item.screenName,
        displayContent: item.comMediumContent?.displayContent || '',
        applicableConId: item.comMediumContent?.applicableConId || item.applicableConId,
        commMediumId: item.commMediumId,
      }));
      setScreenOptions(options);

      if (isEdit && editData?.description) {
        const matched = options?.find(opt => opt.displayContent === editData.description);
        if (matched) setSelectedScreen(matched);
      }
    } catch {
      setScreenOptions([]);
    } finally {
      setScreenLoading(false);
    }
  };

  const handleScreenSelect = (value) => {
    const selected = screenOptions?.find(opt => opt.value === value);
    setSelectedScreen(selected || null);
    if (selected) setDescription(selected.displayContent);
  };

  const handleSubmit = async () => {
    const errs = {};
    if (!title.trim()) errs.title = 'Title is required.';
    if (Object.keys(errs)?.length > 0) { setErrors(errs); return; }

    setErrors({});
    setLoading(true);
    try {
      if (selectedScreen) {
        const loggedUserId = sessionStorage?.getItem('loggedUserId');
        const plainTextContent = description.replace(/<[^>]*>/g, '').trim();
        await Services.upsertDisplayContentPathApi({
          displayContentId: selectedScreen.applicableConId,
          displayContent: plainTextContent,
          isActive: true,
          upsertedBy: loggedUserId,
          commMediumId: selectedScreen.commMediumId,
        });
      }

      await onSubmit({
        title: title.trim(),
        titleBackgroundColor: titleBackgroundColor.trim() || null,
        subTitle: subTitle.trim() || null,
        subTitleBackgroundColor: subTitleBackgroundColor.trim() || null,
        description: isHtmlEmpty(description) ? null : description,
        descriptionBackgroundColor: descriptionBackgroundColor.trim() || null,
        subDescription: isHtmlEmpty(subDescription) ? null : subDescription,
        subDescriptionBackgroundColor: subDescriptionBackgroundColor.trim() || null,
      });
      setTitle(''); setSubTitle(''); setDescription(''); setSubDescription('');
      setSelectedScreen(null);
    } catch {
      setErrors({ title: 'Failed to save. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<span style={{ fontSize: 16, color: '#474747', fontWeight: "bold" }}>{isEdit ? 'Edit Info' : 'Add Info'}</span>}
      visible={visible}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      className="ag-modal"
      width={680}
      bodyStyle={{ padding: '20px 28px 28px', borderTop: "1px solid #bfbfbf", maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      {/* Title & BG Color */}
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 2 }}>
          <label style={{ fontSize: 13, color: '#333' }}>Title <span style={{ color: '#720C20' }}>*</span></label>
          <Input
            placeholder="e.g., Important information"
            value={title}
            onChange={e => { setTitle(e.target.value); if (errors.title) setErrors(p => ({ ...p, title: '' })); }}
            style={{ marginTop: 4, borderRadius: 2, borderColor: errors.title ? '#ff4d4f' : '#720C20', height: 40 }}
          />
          {errors.title && <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4, marginBottom: 0 }}>{errors.title}</p>}
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          <Input placeholder="#ffffff" value={titleBackgroundColor} onChange={e => setTitleBackgroundColor(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 40 }} />
        </div>
      </div>

      {/* Sub Title & BG Color */}
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <div style={{ flex: 2 }}>
          <label style={{ fontSize: 13, color: '#333' }}>Sub Title <span style={{ fontSize: 11, color: '#999' }}>(optional)</span></label>
          <Input placeholder="Sub title" value={subTitle} onChange={e => setSubTitle(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 38 }} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          <Input placeholder="#ffffff" value={subTitleBackgroundColor} onChange={e => setSubTitleBackgroundColor(e.target.value)} style={{ marginTop: 4, borderRadius: 2, height: 38 }} />
        </div>
      </div>

      {/* Screen Name Select */}
      <div style={{ marginTop: 14, marginBottom: 6 }}>
        <label style={{ fontSize: 13, color: '#333' }}>Screen Name</label>
      </div>
      <Select
        allowClear
        showSearch
        placeholder="Select a screen name (optional)"
        value={selectedScreen?.value || undefined}
        onChange={handleScreenSelect}
        loading={screenLoading}
        optionFilterProp="label"
        style={{ width: '100%', marginBottom: 4 }}
        size="large"
        options={screenOptions}
        getPopupContainer={(triggerNode) => triggerNode.parentNode}
      />
      <p style={{ fontSize: 11, color: '#888', margin: '4px 0 0 0' }}>
        Selecting a screen name will load its display content into the description below.
      </p>

      {/* Description & BG Color */}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <label style={{ fontSize: 13, color: '#333' }}>Description</label>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <div style={{ flex: 2, border: '1px solid #720C20', borderRadius: 2, overflow: 'hidden' }}>
            <JoditEditor
              ref={editorRef}
              value={description}
              config={joditConfig}
              onBlur={val => setDescription(val)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Input placeholder="#ffffff" value={descriptionBackgroundColor} onChange={e => setDescriptionBackgroundColor(e.target.value)} style={{ borderRadius: 2, height: 38 }} />
          </div>
        </div>
      </div>

      {/* Sub Description & BG Color */}
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 2 }}>
            <label style={{ fontSize: 13, color: '#333' }}>Sub Description <span style={{ fontSize: 11, color: '#999' }}>(optional)</span></label>
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: '#333' }}>BG Color</label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <div style={{ flex: 2, border: '1px solid #d9d9d9', borderRadius: 2, overflow: 'hidden' }}>
            <JoditEditor
              value={subDescription}
              config={joditConfig}
              onBlur={val => setSubDescription(val)}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Input placeholder="#ffffff" value={subDescriptionBackgroundColor} onChange={e => setSubDescriptionBackgroundColor(e.target.value)} style={{ borderRadius: 2, height: 38 }} />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: isEdit ? 'space-between' : 'flex-end', alignItems: 'center', gap: 10, marginTop: 24 }}>
        {isEdit && onDelete && (
          <Button
            danger
            icon={<DeleteOutlined color='rgb(114, 12, 32)' />}
            onClick={() => setConfirmVisible(true)}
            disabled={loading}
            style={{ borderRadius: 2, fontWeight: 500, display: 'flex', alignItems: 'center', borderColor: "rgb(114, 12, 32)", color: "rgb(114, 12, 32)" }}
          >
            Delete
          </Button>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <Button onClick={onClose} disabled={loading} style={{ borderColor: '#720C20', color: '#720C20', borderRadius: 2, fontWeight: 500 }}>Cancel</Button>
          <Button loading={loading} onClick={handleSubmit} style={{ backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff', borderRadius: 2, fontWeight: 500 }}>
            {isEdit ? 'Update Info' : 'Add Info'}
          </Button>
        </div>
      </div>

      {/* Delete confirmation */}
      <Modal
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        footer={null}
        centered
        width={420}
        zIndex={1200}
        closable
        className="ag-modal"
        bodyStyle={{ padding: '28px 28px 20px' }}
      >
        <p style={{ fontSize: 15, color: '#333', marginBottom: 20, textAlign: 'center' }}>
          Are you sure you want to delete <strong>{editData?.title}</strong>?
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Button onClick={() => setConfirmVisible(false)} style={{ minWidth: 100, height: 36, borderRadius: 4, borderColor: '#720C20', color: '#720C20' }}>Cancel</Button>
          <Button
            onClick={() => { setConfirmVisible(false); onDelete(editData.infoId); }}
            style={{ minWidth: 100, height: 36, borderRadius: 4, backgroundColor: '#720C20', borderColor: '#720C20', color: '#fff' }}
          >Delete</Button>
        </div>
      </Modal>
    </Modal>
  );
};

export default AddInfoModal;
